<template>
    <card-list>
        <subheader-list-item icon="fa-align-left" title="Event information"/>
        <container-list-item padded>
            <div class="columns">
                <div class="column">
                    <b-field label="Event Type" label-position="inside">
                        <b-select expanded v-model="event.type">
                            <option value="event">Event</option>
                            <option value="meal">Meal</option>
                            <option value="playing">Playing Tennis</option>
                            <option value="watching">Watching Tennis</option>
                            <option value="hotel">Hotel</option>
                            <option value="tour">Tour</option>
                            <option value="bus">Bus/Vehicle Transfer</option>
                            <option value="car">Private Transfer</option>
                            <option value="transfer">Other Transfer</option>
                        </b-select>
                    </b-field>
                </div>
            </div>
            <div class="columns">
                <div class="column is-8">
                    <b-field label="Title" label-position="inside">
                        <b-input v-model="event.title"></b-input>
                    </b-field>
                </div>

                <div class="column">
                    <b-field :label="locationLabel" label-position="inside">
                        <b-input v-model="event.location"/>
                    </b-field>
                </div>
            </div>
            <div class="columns">
                <div class="column">
                    <b-field label="Address" label-position="inside">
                        <b-input v-model="event.address"></b-input>
                    </b-field>
                </div>
            </div>
            <div class="columns" v-show="event.type === 'bus' || event.type === 'transfer' || event.type === 'tour' || event.type === 'car'">
                <div class="column">
                    <b-field label="Start/Pickup Location" label-position="inside">
                        <b-input v-model="event.start_location"/>
                    </b-field>
                </div>
            </div>
            <div class="columns">
                <div class="column is-4">
                    <b-field label="Start Date" label-position="inside">
                        <b-input type="date" v-model="event.start_date"/>
                    </b-field>
                </div>
                <div class="column is-3">
                    <b-field label="Start Time" label-position="inside">
                        <b-input type="time" v-model="event.start_time"/>
                    </b-field>
                </div>
                <div class="column">
                    <b-field label="Timezone" label-position="inside">
                        <b-autocomplete
                            :data="startTzResults"
                            field="title"
                            v-model="event.start_tz"
                            @select="selectTZ"
                            icon="search"
                            open-on-focus
                            placeholder="Search...">

                            <template slot="empty">No results.</template>
                        </b-autocomplete>
                    </b-field>
                </div>
            </div>

            <div class="columns" v-show="event.type === 'bus' || event.type === 'transfer' || event.type === 'tour' || event.type === 'car'">
                <div class="column">
                    <b-field label="End/Drop-off Location" label-position="inside">
                        <b-input v-model="event.end_location"/>
                    </b-field>
                </div>
            </div>
            <div class="columns">
                <div class="column is-4">
                    <b-field label="End Date" label-position="inside">
                        <b-input type="date" v-model="event.end_date"/>
                    </b-field>
                </div>
                <div class="column is-3">
                    <b-field label="End Time" label-position="inside">
                        <b-input type="time" v-model="event.end_time"/>
                    </b-field>
                </div>

                <div class="column">
                    <b-field label="Timezone" label-position="inside">
                        <b-autocomplete
                            :data="endTzResults"
                            field="title"
                            v-model="event.end_tz"
                            @select="selectTZ"
                            icon="search"
                            open-on-focus
                            placeholder="Search...">

                            <template slot="empty">No results.</template>
                        </b-autocomplete>
                    </b-field>
                </div>
            </div>
            <div class="columns">
                <div class="column is-4">
                    <b-field label="Participant Count (PAX)" label-position="inside">
                        <b-input v-model="event.pax"/>
                    </b-field>
                </div>
            </div>
            <div class="columns" v-if="!custom">
                <div class="column">
                    <b-switch :true-value="1" :false-value="0" v-model="event.is_hidden">Hidden on Website</b-switch>
                </div>
            </div>
            <div class="columns" v-if="cannedForType.length">
                <div class="column is-6">
                    <b-field label="Insert Saved Description" label-position="inside">
                        <b-select expanded placeholder="Choose a saved description…" v-model="cannedId" @input="applyCanned">
                            <option v-for="d in cannedForType" :key="d.id" :value="d.id">{{ d.title }}</option>
                        </b-select>
                    </b-field>
                </div>
            </div>
            <div class="columns">
                <div class="column">
                    <b-field label="Description">
                        <quill-editor v-model="event.description" ref="descriptionEditor" :options="editorOptions" class="has-background-white content"/>
                    </b-field>
                </div>
            </div>
            <div class="columns">
                <div class="column">
                    <b-field label="Critical Participant Information" label-position="inside">
                        <b-input v-model="event.critical_info"/>
                    </b-field>
                </div>
            </div>
            <div class="columns">
                <div class="column">
                    <b-field label="Vendor Information">
                        <quill-editor v-model="event.vendor_info" ref="vendorEditor" :options="editorOptions" class="has-background-white content"/>
                    </b-field>
                </div>
            </div>
        </container-list-item>
        <!-- The "Event contact" and "Meal booking" contact blocks that used to
             sit here are gone: contacts are real records now, added from the
             Contacts tab of the event card, and one restaurant is one
             directory row rather than a copy per event. Menu/Wines stay --
             they are meal data, not contact data, and the Navigator Menu &
             Wine page is compiled from them. -->
        <subheader-list-item icon="fa-utensils" title="Menu &amp; wines" v-show="event.type === 'meal'"/>
        <container-list-item padded v-show="event.type === 'meal'">
            <div class="columns">
                <div class="column">
                    <b-field label="Menu">
                        <quill-editor v-model="event.menu" ref="menuEditor" :options="editorOptions" class="has-background-white content"/>
                    </b-field>
                </div>
            </div>
            <div class="columns">
                <div class="column">
                    <b-field label="Wines">
                        <quill-editor v-model="event.wines" ref="winesEditor" :options="editorOptions" class="has-background-white content"/>
                    </b-field>
                </div>
            </div>
            <div class="columns">
                <div class="column">
                    <b-switch :true-value="1" :false-value="0" v-model="event.dietary_sent">Dietary Sent</b-switch>
                </div>
                <div class="column">
                    <b-switch :true-value="1" :false-value="0" v-model="event.menu_selected">Menu Selected</b-switch>
                </div>
                <div class="column">
                    <b-switch :true-value="1" :false-value="0" v-model="event.wine_selected">Wine Selected</b-switch>
                </div>
            </div>
        </container-list-item>
        <subheader-list-item icon="fa-file-pdf" title="Documents"/>
        <container-list-item padded v-if="!event.id">
            <i class="far fa-info-circle mr-3 ml-2 fa-fw"/>Please save this event before adding documents.
        </container-list-item>

        <template v-else>
            <document-list-item
                v-if="!custom"
                v-for="doc in event.documents"
                :doc="doc"
                :card="card"
                :active="(child && child.definition.component === 'edit-event-document') && child.props.docId === doc.id.toString()"
                @click="$openCard('edit-event-document', {tripId: tripId, dayId: event.day_id, eventId: event.id, docId: doc.id}, card)"
            />
            <document-list-item
                v-else
                v-for="doc in event.documents"
                :doc="doc"
                :card="card"
                :active="(child && child.definition.component === 'edit-event-document') && child.props.docId === doc.id.toString()"
                @click="$openCard('edit-event-document', {tripId: 'null', dayId: event.day_id, eventId: event.id, docId: doc.id, type: 'custom-event', registrationId: registrationId}, card)"
            />
        </template>


        <container-list-item padded v-if="event.id && event.documents.length === 0">
            <i class="far fa-info-circle mr-3 ml-2 fa-fw"/>No event documents.
        </container-list-item>
    </card-list>
</template>

<script>
    import CardList from '../../TIER/components/CardList';
    import SubheaderListItem from '../../TIER/components/SubheaderListItem';
    import ContainerListItem from '../../TIER/components/ContainerListItem';

    import moment from 'moment-timezone';
    import DocumentListItem from "../../components/DocumentListItem.vue";
    import {client as http} from '../../http_client';

    export default {
        name: 'EventForm',
        props: {
            event: {
                type: Object,
                required: true
            },
            tripId: {
                type: Number,
                required: false
            },
            registrationId: {
                type: Number,
                required: false
            },
            custom: {
                type: Boolean,
                default: false
            },
            card: {
                type: Object,
                required: true
            },
            child: {
                required: true
            }
        },
        components: {DocumentListItem, ContainerListItem, SubheaderListItem, CardList},
        data: function() {
            return {
                zones: moment.tz.names(),
                // Saved descriptions (Settings > Event Descriptions) offered on the
                // picker above the Description editor. Replaces the old hard-coded
                // auto-populate that fired when the type switched to 'car'.
                savedDescriptions: [],
                cannedId: null,
                // Curated rich-text toolbar (TEN-80) shared by the customer-facing
                // Description and the staff-only Vendor Information fields, so editors can
                // Bold and red-highlight key details. Replaces Quill's sprawling default
                // toolbar with just the formatting the client asked for. The stored HTML
                // rides down in the itinerary JSON and renders as rich text in Navigator.
                editorOptions: {
                    modules: {
                        toolbar: [
                            ['bold', 'italic', 'underline'],
                            // Text color includes white + black so editors can keep text
                            // legible on a colored background (e.g. white on red highlight).
                            [{ color: ['#000000', '#e60000', '#ffffff'] }, { background: ['#ffeb3b', '#ff9800', '#e60000'] }],
                            ['clean']
                        ]
                    }
                }
            };
        },
        computed: {
            // Same `location` column either way — meals just call it what it is.
            locationLabel() {
                return this.event.type === 'meal' ? 'Restaurant' : 'Location';
            },
            cannedForType() {
                // All-types descriptions plus those scoped to the current event type.
                return this.savedDescriptions.filter(d => !d.event_type || d.event_type === this.event.type);
            },
            startTzResults() {
                if (this.event && this.event.start_tz) {
                    return this.zones.filter((option) => {
                        return option
                            .toString()
                            .toLowerCase()
                            .indexOf(this.event.start_tz.toLowerCase()) >= 0;
                    });
                } else {
                    return null;
                }
            },
            endTzResults() {
                if (this.event && this.event.end_tz) {
                    return this.zones.filter((option) => {
                        return option
                            .toString()
                            .toLowerCase()
                            .indexOf(this.event.end_tz.toLowerCase()) >= 0;
                    });
                } else {
                    return null;
                }
            }
        },
        methods: {
            selectTZ(option) {
                // console.log('Selected', moment().tz(option).format('MMMM Do YYYY, h:mm:ss a Z'));
            },
            applyCanned(id) {
                const canned = this.savedDescriptions.find(d => d.id === id);
                if (canned) {
                    this.event.description = canned.description;
                    // Clear the select so re-picking the same item still fires @input
                    // after manual edits. The null re-entry no-ops above.
                    this.$nextTick(() => {
                        this.cannedId = null;
                    });
                }
            }
        },
        mounted() {
            http.get('/api/event_descriptions', {force: true}).then(response => {
                this.savedDescriptions = response.data;
            });
        }
    };
</script>
